import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { PALS, ELEMENT_COLORS, type Pal } from "@/lib/pals-data";
import { TEAMS, TEAM_PURPOSES, analyzeTeam, type TeamPurpose } from "@/lib/team-data";
import { PalPortrait } from "@/components/PalPortrait";
import { ShareButton } from "@/components/ShareButton";
import { FaqSection, RelatedGuides, faqSchema, type FaqItem } from "@/components/PageFaq";
import { snippetAnswerSchema } from "@/lib/schema";
import { SnippetFunnel } from "@/components/SnippetFunnel";
import { SNIPPETS } from "@/lib/snippet-copy";
import { ContextVisualStrip } from "@/components/ContextVisualStrip";
import { CrossCategoryLinks } from "@/components/CrossCategoryLinks";

const TITLE = "Palworld Team Builder & Team Database — Pal Stars";
const DESC =
  "Build a 5-Pal Palworld team and check element, work and mount coverage, or copy a ready-made PvE, PvP, boss, base or farming squad.";
const URL = "https://palstars.com/team-builder";

const FAQ: FaqItem[] = [
  {
    q: "How many Pals can be on a Palworld team?",
    a: "Five Pals can be in your active party at once. Everything else stays in the Pal Box or works at a base, which is why a good squad assigns one clear job to each of the five slots instead of stacking similar Pals.",
  },
  {
    q: "What is the best team in Palworld?",
    a: "The strongest general-purpose team is one main damage Pal, a second attacker of a different element, a fast flying mount, a gathering Pal and a high-defence tank. That spread covers combat, travel and loot so you never break a run to return to base.",
  },
  {
    q: "How does this Palworld team builder work?",
    a: "Pick up to five Pals from the roster and the builder reports your average PvE, PvP, base-work and mount scores, every element you cover, all work suitabilities in the squad, and whether you are carrying a usable mount.",
  },
  {
    q: "Should base worker Pals go in my party?",
    a: "No. Base Pals only work while assigned to a base, so party slots should hold combat, mount and gathering Pals. Build a separate five-Pal production squad covering Kindling, Watering, Planting, Handiwork and Transporting.",
  },
  {
    q: "How many elements should a Palworld team cover?",
    a: "At least three. Every tower boss and most Alphas resist one element, so three distinct elements across five slots guarantees you always have a neutral or advantaged attacker without rebuilding the party.",
  },
];

export const Route = createFileRoute("/team-builder")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      faqSchema(FAQ, URL),
      snippetAnswerSchema({ question: SNIPPETS.teams.question, answer: SNIPPETS.teams.answer, path: "/team-builder" }),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Palworld Team Database — Pal Stars",
          numberOfItems: TEAMS.length,
          itemListElement: TEAMS.map((t, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: `${t.name} — ${t.purpose} team`,
            url: `${URL}#${t.slug}`,
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: { "@id": "https://palstars.com/", name: "Home" } },
            { "@type": "ListItem", position: 2, name: "Team Builder", item: { "@id": URL, name: "Team Builder" } },
          ],
        }),
      },
    ],
  }),
  component: TeamBuilderPage,
});

const SQUAD_KEY = "palstars:squad";

function TeamBuilderPage() {
  const [purpose, setPurpose] = useState<TeamPurpose | "">("");
  const [q, setQ] = useState("");
  const [squad, setSquad] = useState<Pal[]>([]);

  // Restore the squad after hydration so opening a Pal card page and coming
  // back with the Back button leaves the party exactly where it was.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(SQUAD_KEY);
      if (!raw) return;
      const slugs = JSON.parse(raw) as string[];
      const restored = slugs.map((s) => PALS.find((p) => p.slug === s)).filter((p): p is Pal => Boolean(p));
      if (restored.length) setSquad(restored.slice(0, 5));
    } catch {
      /* ignore malformed storage */
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(SQUAD_KEY, JSON.stringify(squad.map((p) => p.slug)));
    } catch {
      /* storage unavailable */
    }
  }, [squad]);

  const comps = useMemo(
    () => TEAMS.filter((t) => !purpose || t.purpose === purpose),
    [purpose],
  );

  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    return PALS.filter((p) => p.name.toLowerCase().includes(needle)).slice(0, 8);
  }, [q]);

  const report = useMemo(() => analyzeTeam(squad), [squad]);

  const add = (p: Pal) => {
    setSquad((s) => (s.length >= 5 || s.some((x) => x.id === p.id) ? s : [...s, p]));
    setQ("");
  };
  const remove = (id: number) => setSquad((s) => s.filter((p) => p.id !== id));
  const clearSquad = () => setSquad([]);


  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
      <h1 className="text-3xl sm:text-4xl font-bold">Palworld Team Builder</h1>
      <p className="aeo-answer text-muted-foreground mt-2 max-w-3xl">
        A Palworld party holds five Pals. Build your own squad below and see element, work-suitability and
        mount coverage instantly, or copy one of the {TEAMS.length} ready-made team comps from the team
        database — PvE, PvP, boss rush, base production, ore farming and exploration.
      </p>

      <SnippetFunnel className="mt-6" {...SNIPPETS.teams} />
      <ContextVisualStrip variant="teams" />
      <div className="mt-4">
        <ShareButton title={TITLE} text="Build a 5-Pal Palworld team and check element + work coverage." path="/team-builder" />
      </div>

      {/* ---------- Interactive builder ---------- */}
      <section className="glass rounded-2xl p-6 mt-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-xl font-bold">Build your squad ({squad.length}/5)</h2>
          {squad.length > 0 && (
            <button
              onClick={clearSquad}
              className="rounded-lg border px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:bg-card hover:text-foreground"
            >
              Clear squad
            </button>
          )}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Your squad is saved on this device — open a Pal card for full stats and use the Back button to return to
          the exact squad you were building.
        </p>

        <div className="mt-3 relative max-w-md">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search a Pal to add…"
            aria-label="Search Pals to add to your team"
            className="w-full rounded-lg bg-background/50 border px-4 py-2 text-sm outline-none focus:ring-2 ring-primary"
          />
          {matches.length > 0 && (
            <ul className="absolute z-20 mt-1 w-full rounded-lg border bg-card shadow-lg overflow-hidden">
              {matches.map((p) => (
                <li key={p.id}>
                  <button
                    onClick={() => add(p)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-background/60 flex items-center justify-between gap-2"
                  >
                    <span>{p.name}</span>
                    <span className="text-xs text-muted-foreground">{p.element}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-5">
          {Array.from({ length: 5 }).map((_, i) => {
            const p = squad[i];
            if (!p) {
              return (
                <div key={`slot-${i}`} className="rounded-xl border border-dashed p-3 text-center text-xs text-muted-foreground min-h-[180px] flex items-center justify-center">
                  Slot {i + 1} empty
                </div>
              );
            }
            return (
              <div key={`slot-${i}`} className="relative rounded-xl border p-3">
                <button
                  onClick={() => remove(p.id)}
                  aria-label={`Remove ${p.name} from your squad`}
                  title={`Remove ${p.name}`}
                  className="absolute right-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded-full border bg-card text-xs font-bold text-muted-foreground hover:bg-destructive hover:text-destructive-foreground"
                >
                  ×
                </button>
                <PalPortrait pal={p} size={180} />
                <div className="mt-2 text-sm font-semibold truncate">{p.name}</div>
                <div className="text-[10px]" style={{ color: ELEMENT_COLORS[p.element] }}>{p.element}</div>
                <div className="mt-2 flex gap-2">
                  <Link to="/pals/$slug" params={{ slug: p.slug }} className="text-xs text-primary hover:underline">
                    Card & stats
                  </Link>
                  <button onClick={() => remove(p.id)} className="text-xs text-muted-foreground hover:text-foreground">
                    Remove
                  </button>
                </div>
              </div>
            );

          })}
        </div>

        {squad.length > 0 && (
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {([
              ["PvE combat", report.pve],
              ["PvP ladder", report.pvp],
              ["Base work", report.base],
              ["Mount speed", report.mount],
            ] as const).map(([label, v]) => (
              <div key={label} className="rounded-xl border p-3">
                <div className="text-xs text-muted-foreground">{label}</div>
                <div className="text-lg font-bold">{v.toFixed(1)}/10</div>
              </div>
            ))}
            <div className="rounded-xl border p-3 sm:col-span-2">
              <div className="text-xs text-muted-foreground">Element coverage ({report.elements.length})</div>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {report.elements.map((el) => (
                  <span key={el} className="rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ background: `${ELEMENT_COLORS[el]}22`, color: ELEMENT_COLORS[el] }}>
                    {el}
                  </span>
                ))}
              </div>
            </div>
            <div className="rounded-xl border p-3 sm:col-span-2">
              <div className="text-xs text-muted-foreground">Work suitabilities covered</div>
              <div className="mt-1 text-sm">{report.work.join(", ") || "None"}</div>
              <div className="mt-1 text-xs" style={{ color: report.hasMount ? "#22C55E" : "#EF4444" }}>
                {report.hasMount ? "Mount slot covered" : "No usable mount — add a flier"}
              </div>
            </div>
          </div>
        )}
      </section>

      {/* ---------- Team database ---------- */}
      <section className="mt-12">
        <h2 className="text-2xl font-bold">Palworld team database</h2>
        <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Filter teams by purpose">
          <button
            onClick={() => setPurpose("")}
            className={`rounded-full border px-3 py-1 text-xs font-semibold ${purpose === "" ? "bg-primary text-primary-foreground" : "hover:bg-card"}`}
          >
            All teams
          </button>
          {TEAM_PURPOSES.map((p) => (
            <button
              key={p}
              onClick={() => setPurpose(purpose === p ? "" : p)}
              aria-pressed={purpose === p}
              className={`rounded-full border px-3 py-1 text-xs font-semibold ${purpose === p ? "bg-primary text-primary-foreground" : "hover:bg-card"}`}
            >
              {p}
            </button>
          ))}
        </div>

        <div className="grid gap-6 mt-6">
          {comps.map((t) => (
            <article key={t.slug} id={t.slug} className="glass rounded-2xl p-6">
              <span className="text-[10px] uppercase tracking-widest text-primary">{t.purpose} team</span>
              <h3 className="mt-1 text-xl font-bold">{t.name}</h3>
              <p className="aeo-answer mt-1 text-sm text-muted-foreground max-w-3xl">{t.desc}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-4">
                {t.slots.map((s) => (
                  <div key={s.role} className="rounded-xl border p-3">
                    <div className="text-[10px] uppercase tracking-wide text-primary">{s.role}</div>
                    <Link to="/pals/$slug" params={{ slug: s.pal.slug }} className="block mt-2 card-hover">
                      <PalPortrait pal={s.pal} size={180} />
                      <div className="mt-2 text-sm font-semibold truncate">{s.pal.name}</div>
                    </Link>
                    <div className="text-[10px]" style={{ color: ELEMENT_COLORS[s.pal.element] }}>{s.pal.element}</div>
                    <p className="mt-1 text-[11px] text-muted-foreground leading-snug">{s.note}</p>
                    <button
                      onClick={() => add(s.pal)}
                      className="mt-2 text-[11px] text-primary hover:underline"
                    >
                      Add to my squad
                    </button>
                  </div>
                ))}
              </div>
              <ul className="mt-4 grid gap-1.5 sm:grid-cols-3">
                {t.tips.map((tip) => (
                  <li key={tip} className="text-xs text-muted-foreground">• {tip}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <CrossCategoryLinks side="game" />

      <FaqSection items={FAQ} heading="Palworld team building FAQ" />

      <RelatedGuides
        links={[
          { slug: "palworld-team-building-guide", label: "Palworld Team Building Guide & FAQ" },
          { slug: "palworld-all-bosses-guide", label: "All Palworld Bosses Guide & FAQ" },
          { slug: "palworld-breeding-tier-list-guide", label: "Palworld Breeding Tier List Guide" },
        ]}
      />
    </div>
  );
}
